// [STREETS]: 4 required fields plus 3 optional trailing groups (gutter
// depression+width, sides, backing width+slope+roughness). This port always
// emits the full 10-column row (default 0, sides default 2) rather than
// tracking presence/absence — a minimal .inp still reads back the same
// defaults the real engine applies.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface Street {
  name: string;
  roadWidth: number; // Tcrown
  curbHeight: number; // Hcurb
  crossSlope: number; // Sx, %
  roughness: number; // nRoad
  gutterDepression: number; // Hdep
  gutterWidth: number; // Wg
  sides: 1 | 2;
  backingWidth: number; // Tback
  backingSlope: number; // Sback
  backingRoughness: number; // nBack
}

export function readStreets(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Street[] {
  const streets: Street[] = [];
  for (const row of raw.get("STREETS") ?? []) {
    const name = row[0];
    if (!name) continue;
    const ctx = (field: string) => `[STREETS] "${name}" ${field}`;
    streets.push({
      name,
      roadWidth: parseNum(row[1], 0, ctx("Tcrown"), warnings),
      curbHeight: parseNum(row[2], 0, ctx("Hcurb"), warnings),
      crossSlope: parseNum(row[3], 0, ctx("Sx"), warnings),
      roughness: parseNum(row[4], 0, ctx("nRoad"), warnings),
      gutterDepression: parseNum(row[5], 0, ctx("Hdep"), warnings),
      gutterWidth: parseNum(row[6], 0, ctx("Wg"), warnings),
      sides: row[7] === "1" ? 1 : 2,
      backingWidth: parseNum(row[8], 0, ctx("Tback"), warnings),
      backingSlope: parseNum(row[9], 0, ctx("Sback"), warnings),
      backingRoughness: parseNum(row[10], 0, ctx("nBack"), warnings),
    });
  }
  return streets;
}

export function writeStreets(
  raw: ReadonlyMap<string, string[][]>,
  streets: Street[]
): Map<string, string[][]> {
  const rows = streets.map((s) => [
    s.name,
    String(s.roadWidth),
    String(s.curbHeight),
    String(s.crossSlope),
    String(s.roughness),
    String(s.gutterDepression),
    String(s.gutterWidth),
    String(s.sides),
    String(s.backingWidth),
    String(s.backingSlope),
    String(s.backingRoughness),
  ]);
  return writeOrDeleteSection(raw, "STREETS", rows);
}

export function nextAvailableStreetName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "ST");
}
